import type { ProductInfo } from "@artis/sdk";
import { useProductsProducer } from "@artis/sdk-react";
import { defaultFieldNameSelector, type TProductArtisType } from "@package/grid-utils";
import { useEffect, useRef } from "react";

import { useFeatureFlags } from "../context/flags";
import { useAllProducts } from "../data/products";
import { PRA_MOCK_PRODUCTS } from "../data/pra/praMock";
import { selectorsData } from "../webapp/market-grid/selectorsData";

const selectorsByExchange = selectorsData as Record<
  string | number,
  Record<string, { formula?: readonly string[] }> | undefined
>;

const fieldsFor = (
  artisType: TProductArtisType,
  exchange: string | number | null | undefined,
  maturity: string | null | undefined,
): readonly string[] | undefined => {
  if (artisType === "eod" || artisType === "customer_curve") return ["value"];
  if (exchange == null || maturity == null) return undefined;
  const formula = selectorsByExchange[exchange]?.[maturity]?.formula;
  return formula && formula.length > 0 ? formula : undefined;
};

/** Feeds `artis.v1.products` from the webapp product catalog. Mount once inside the Artis provider. */
export const ProductsPublisher = () => {
  const publish = useProductsProducer();
  const products = useAllProducts();
  const [praPage] = useFeatureFlags("pra-page");
  const praMock = Boolean(praPage);
  const praPublished = useRef(false);

  useEffect(() => {
    const data = products.data;
    if (!data) return;
    const upserts: ProductInfo[] = [];
    for (const p of Object.values(data)) {
      if (!p?.id || !p?.name) continue;
      upserts.push({
        id: p.id,
        kind: "curve",
        name: p.name,
        description: p.description ?? undefined,
        defaultField: p.artis_type ? defaultFieldNameSelector(p.artis_type) : undefined,
        fields: p.artis_type
          ? fieldsFor(p.artis_type, p.packageByPackage?.sourceBySource?.exchange?.code, p.maturity)
          : undefined,
      });
    }
    if (praMock) upserts.push(...PRA_MOCK_PRODUCTS);
    const removes = praPublished.current && !praMock ? PRA_MOCK_PRODUCTS.map((p) => p.id) : [];
    praPublished.current = praMock;
    if (upserts.length > 0 || removes.length > 0) publish({ upserts, ...(removes.length > 0 ? { removes } : {}) });
  }, [publish, products.data, praMock]);

  return null;
};
